import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  RefreshCw,
  Search,
  QrCode,
  Printer,
  Loader2,
  Car,
  DollarSign,
  Inbox,
  Sparkles,
  ExternalLink,
  CheckSquare,
  Square,
  X,
  Headset,
  ScanLine,
  Archive,
  Wrench,
  PrinterCheck,
  Clock,
  BellRing,
  Building2,
  ClipboardList,
} from "lucide-react";
import { api, currency, priceLabel, timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import QRModal from "@/components/QRModal";
import AsIsDialog from "@/components/AsIsDialog";
import InsiderDialog, { insiderCount } from "@/components/InsiderDialog";
import { StoreChip } from "@/components/StoreChip";
import { StockNumberField } from "@/components/StockNumberField";

const NEW_DAYS = 5;
const VIEWS = {
  all: { label: "All" },
  new: { label: `New arrivals (${NEW_DAYS}d)`, icon: Clock, days: NEW_DAYS, cls: "border-emerald-500 bg-emerald-950/40 text-emerald-300 hover:bg-emerald-950/60 font-semibold" },
  unprinted: { label: "Not printed", icon: Printer, cls: "border-[#0A8CF2] bg-[#0A8CF2]/15 text-[#7FC3FF] hover:bg-[#0A8CF2]/25 font-semibold" },
  asis: { label: "As-Is", icon: Wrench, cls: "border-amber-500 bg-amber-950/40 text-amber-300 hover:bg-amber-950/60 font-semibold" },
  unassigned: { label: "No store", icon: Building2, cls: "border-amber-500 bg-amber-950/40 text-amber-300 hover:bg-amber-950/60 font-semibold" },
  archived: { label: "Archived", icon: Archive, cls: "border-amber-500 bg-amber-950/40 text-amber-300 hover:bg-amber-950/60 font-semibold" },
};
const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

// "VIN1, NT26632A, 620939" → which pasted codes matched a loaded vehicle
function codeMatches(q, vehicles) {
  const terms = q.split(",").map((t) => t.trim()).filter(Boolean);
  if (!q.includes(",") || terms.length === 0) return null;
  const hay = vehicles.map((v) => `${v.vin || ""} ${v.stock_number || ""}`.toLowerCase());
  const missing = terms.filter((t) => !hay.some((h) => h.includes(t.toLowerCase())));
  return { terms, missing };
}

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="tracing-top rounded-xl border border-[#2A2E3D] bg-[#12141C] p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-slate-500 font-mono-tech">{label}</span>
        <Icon className={`h-4 w-4 ${accent || "text-[#0A8CF2]"}`} />
      </div>
      <div className="mt-2 font-heading text-3xl font-black">{value}</div>
    </div>
  );
}

export default function Inventory() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [condition, setCondition] = useState("");
  const [view, setView] = useState("all");   // all | new | unprinted | asis | archived
  const archived = view === "archived";
  const storeView = view === "unassigned" ? "unassigned" : view.startsWith("store:") ? view.slice(6) : null;
  const [sort, setSort] = useState("-created_at");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [enrichState, setEnrichState] = useState(null);
  const [qrVehicle, setQrVehicle] = useState(null);
  const [asIsVehicle, setAsIsVehicle] = useState(null);
  const [insiderVehicle, setInsiderVehicle] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [stores, setStores] = useState([]);   // rooftops sharing this inventory (lead routing)
  const [walls, setWalls] = useState(null);   // which insider walls are on in Settings
  useEffect(() => {
    api.get("/dealer").then(({ data }) => {
      setStores(data.stores || []);
      setWalls({ recon: !!data.wall_recon, time_on_lot: !!data.wall_time_on_lot, acquisition: !!data.wall_acquisition });
    }).catch(() => {});
  }, []);

  const toggleSelect = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const selectAllShown = () => setSelected(new Set(vehicles.map((v) => v.id)));
  const clearSelection = () => setSelected(new Set());
  const allShownSelected = vehicles.length > 0 && vehicles.every((v) => selected.has(v.id));
  const printSelectedHref = `/sticker/selected?ids=${[...selected].join(",")}`;

  const loadStats = useCallback(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (condition) params.set("condition", condition);
      if (sort) params.set("sort", archived ? "-archived_at" : sort);
      if (archived) params.set("active", "false");
      if (view === "unprinted") params.set("printed", "false");
      if (view === "asis") params.set("as_is", "true");
      if (view === "unassigned") params.set("store", "unassigned");
      if (view.startsWith("store:")) params.set("store", view.slice(6));
      if (view === "new") params.set("new_days", String(VIEWS.new.days));
      const { data } = await api.get(`/vehicles?${params.toString()}`);
      setVehicles(data);
    } catch {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  }, [q, condition, sort, view, archived]);

  const replaceVehicle = (u) => setVehicles((list) => list.map((x) => (x.id === u.id ? u : x)));
  const markPrinted = async (v, printed) => {
    try {
      await api.post("/vehicles/mark-printed", { ids: [v.id], printed });
      replaceVehicle({ ...v, printed_at: printed ? new Date().toISOString() : null, print_count: (v.print_count || 0) + (printed ? 1 : 0) });
      loadStats();
    } catch {
      toast.error("Couldn't update print status");
    }
  };

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const watchEnrichment = useCallback(() => {
    let ticks = 0;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get("/inventory/sync/status");
        ticks += 1;
        if (data.status === "enriching") {
          setEnrichState(data);
          if (ticks % 4 === 0) load();
          return;
        }
        clearInterval(iv);
        setEnrichState(null);
        if (data.status === "idle") toast.success(data.message || "Inventory refreshed");
        else if (data.status === "error") toast.error(data.message || "Sync failed");
        load();
        loadStats();
      } catch {
        clearInterval(iv);
        setEnrichState(null);
      }
    }, 8000);
  }, [load, loadStats]);

  const [archiveNote, setArchiveNote] = useState("");
  useEffect(() => {
    api.get("/inventory/sync/status").then(({ data }) => {
      setArchiveNote(data.archive_skipped ? data.archive_skip_reason || "Archiving was skipped on the last scan." : "");
      if (data.status === "enriching") {
        setEnrichState(data);
        watchEnrichment();
      }
    }).catch(() => {});
  }, [watchEnrichment, enrichState?.status, syncing]);

  const pollSync = useCallback(() => {
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get("/inventory/sync/status");
        if (data.status === "running") return;
        clearInterval(iv);
        setSyncing(false);
        if (data.status === "enriching") {
          setEnrichState(data);
          toast.success(data.message || "Inventory found — enriching in the background");
          watchEnrichment();
        } else if (data.status === "idle") {
          toast.success(data.message || "Sync complete");
        } else if (data.status === "error") {
          toast.error(data.message || "Sync failed");
        }
        load();
        loadStats();
      } catch {
        clearInterval(iv);
        setSyncing(false);
      }
    }, 2500);
  }, [load, loadStats, watchEnrichment]);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post("/inventory/sync");
      toast.info("Inventory sync started…");
      pollSync();
    } catch {
      setSyncing(false);
      toast.error("Could not start sync");
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Inventory</h1>
          <p className="text-sm text-slate-400 mt-1">
            Live vehicles synced from your dealership site · generate QR window stickers.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {storeView ? (
            <a href={`/sticker/all?store=${encodeURIComponent(storeView === "unassigned" ? "unassigned" : storeView)}`} target="_blank" rel="noreferrer">
              <Button
                variant="outline"
                data-testid="print-store-btn"
                title="Print every sticker for this store"
                className="border-[#0A8CF2]/60 bg-[#0A8CF2]/10 hover:bg-[#0A8CF2]/25 text-[#7FC3FF] font-semibold"
              >
                <Printer className="mr-2 h-4 w-4" /> Print {storeView === "unassigned" ? "unassigned" : stores.find((s) => s.id === storeView)?.name || "store"} ({vehicles.length})
              </Button>
            </a>
          ) : (
            <a href="/sticker/all" target="_blank" rel="noreferrer">
              <Button
                variant="outline"
                data-testid="print-all-stickers-btn"
                className="border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"
              >
                <Printer className="mr-2 h-4 w-4" /> Print all
              </Button>
            </a>
          )}
          {selected.size > 0 ? (
            <a href={printSelectedHref} target="_blank" rel="noreferrer">
              <Button
                variant="outline"
                data-testid="print-selected-btn"
                className="border-[#0A8CF2]/60 bg-[#0A8CF2]/10 hover:bg-[#0A8CF2]/25 text-[#7FC3FF] font-semibold"
              >
                <Printer className="mr-2 h-4 w-4" /> Print selected ({selected.size})
              </Button>
            </a>
          ) : (
            <Button
              variant="outline"
              disabled
              data-testid="print-selected-btn"
              className="border-slate-800 bg-slate-900/30 text-slate-500"
            >
              <Printer className="mr-2 h-4 w-4" /> Print selected (0)
            </Button>
          )}
          <Button
            onClick={sync}
            disabled={syncing}
            data-testid="sync-inventory-btn"
            className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold shadow-lg shadow-blue-900/40"
          >
            {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            {syncing ? "Syncing…" : "Sync now"}
          </Button>
        </div>
      </div>

      {enrichState && (
        <div data-testid="sync-progress" className="mb-6 flex items-center gap-3 rounded-xl border border-[#0A8CF2]/40 bg-[#0A8CF2]/10 px-4 py-3 text-sm">
          <Loader2 className="h-4 w-4 animate-spin text-[#7FC3FF] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{enrichState.message}</div>
            <div className="mt-1.5 h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-[#0A8CF2] transition-all" style={{ width: `${enrichState.enrich_total ? Math.min(100, Math.round((100 * (enrichState.enriched || 0)) / enrichState.enrich_total)) : 5}%` }} />
            </div>
          </div>
          <span className="font-mono-tech text-xs text-slate-400 shrink-0" data-testid="sync-progress-count">
            {enrichState.enriched || 0}/{enrichState.enrich_total || "…"}
          </span>
        </div>
      )}

      {archiveNote && (
        <div data-testid="archive-skipped-note" className="mb-6 flex items-start gap-3 rounded-xl border border-amber-700/50 bg-amber-950/30 px-4 py-3 text-sm text-amber-200">
          <Archive className="h-4 w-4 mt-0.5 shrink-0 text-amber-400" />
          <div><span className="font-semibold">Overnight archive skipped.</span> {archiveNote}</div>
        </div>
      )}

      {stats && (stats.new_since_visit > 0 || stats.unprinted_count > 0) && !archived && (
        <div data-testid="since-visit-banner" className="mb-6 rounded-xl border border-emerald-700/40 bg-gradient-to-r from-emerald-950/40 to-transparent px-5 py-4 flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <BellRing className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <div className="font-heading font-bold">
                {stats.new_since_visit > 0
                  ? <><span className="text-emerald-300" data-testid="since-visit-new-count">{stats.new_since_visit}</span> new vehicle{stats.new_since_visit === 1 ? "" : "s"} since your last visit</>
                  : "You're caught up on new arrivals"}
                {stats.unprinted_count > 0 && <> · <span className="text-[#7FC3FF]" data-testid="since-visit-unprinted-count">{stats.unprinted_count}</span> still need{stats.unprinted_count === 1 ? "s" : ""} a sticker</>}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                {user?.previous_login_at ? `Last signed in ${new Date(user.previous_login_at).toLocaleString()}.` : `First sign-in — showing the last ${NEW_DAYS} days.`}{" "}
                Stickers are marked printed when you hit Print on the sticker page.
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {stats.new_since_visit > 0 && (
              <Button variant="outline" onClick={() => { setView("new"); setCondition(""); }} data-testid="since-visit-show-new" className="h-9 border-emerald-700/60 bg-emerald-950/40 hover:bg-emerald-950/70 text-emerald-200">
                <Clock className="mr-1.5 h-4 w-4" /> Show new arrivals
              </Button>
            )}
            {stats.unprinted_count > 0 && (
              <>
                <Button variant="outline" onClick={() => { setView("unprinted"); setCondition(""); }} data-testid="since-visit-show-unprinted" className="h-9 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200">
                  <Printer className="mr-1.5 h-4 w-4" /> Show not printed
                </Button>
                <a href={`/sticker/unprinted${storeView ? `?store=${encodeURIComponent(storeView)}` : ""}`} target="_blank" rel="noreferrer">
                  <Button data-testid="print-unprinted-btn" className="h-9 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
                    <PrinterCheck className="mr-1.5 h-4 w-4" /> Print all unprinted{storeView ? ` · ${storeView === "unassigned" ? "unassigned" : stores.find((s) => s.id === storeView)?.name || "store"}` : ` (${stats.unprinted_count})`}
                  </Button>
                </a>
              </>
            )}
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard icon={Car} label="Active vehicles" value={stats.total_vehicles} />
          <StatCard icon={DollarSign} label="Avg price" value={currency(stats.avg_price)} />
          <StatCard icon={Sparkles} label="Specialists" value={stats.specialists} accent="text-emerald-400" />
          <StatCard icon={Inbox} label="New leads" value={stats.leads_new} accent="text-amber-400" />
          <StatCard icon={ScanLine} label="QR scans · 30 days" value={stats.scans_30d ?? 0} accent="text-[#7FC3FF]" testId="stat-scans-30d" />
          {stats.specialist_match !== "active" && (
            <StatCard icon={Headset} label="Asked for a specialist" value={stats.match_requests ?? 0} accent="text-[#7FC3FF]" testId="stat-match-requests" />
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 mb-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              data-testid="inventory-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search model, trim, VIN, stock, color… or paste several VINs / stock #s separated by commas"
              className="pl-9 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setSort(sort === "-scan_count" ? "-created_at" : "-scan_count")}
              data-testid="sort-most-scanned"
              aria-pressed={sort === "-scan_count"}
              title="Sort by QR scans"
              className={
                sort === "-scan_count"
                  ? "border-[#0A8CF2] bg-[#0A8CF2]/15 text-[#7FC3FF] hover:bg-[#0A8CF2]/25 font-semibold"
                  : "border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"
              }
            >
              <ScanLine className="mr-1.5 h-4 w-4" /> Most scanned
            </Button>
            {["", "New", "Used"].map((c) => (
              <Button
                key={c || "all"}
                variant={condition === c && (view === "all" || storeView) ? "default" : "outline"}
                onClick={() => { setCondition(c); if (!storeView) setView("all"); }}
                data-testid={`filter-${c || "all"}`}
                title={storeView ? `${c || "All"} units in the selected store` : undefined}
                className={
                  condition === c && (view === "all" || storeView)
                    ? "bg-[#0A8CF2] hover:bg-[#3AA3FF] font-semibold"
                    : "border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"
                }
              >
                {c || "All"}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {stores.map((s) => {
            const key = `store:${s.id}`;
            const on = view === key;
            const cc = stats?.store_condition_counts?.[s.id];
            return (
              <Button key={key} variant="outline" onClick={() => setView(on ? "all" : key)} data-testid={`filter-store-${s.id}`} aria-pressed={on}
                title={`Units whose leads route to ${s.name}${cc ? ` — ${cc.New} new · ${cc.Used} used` : ""}`}
                className={`h-auto py-1.5 ${on ? "border-[#0A8CF2] bg-[#0A8CF2]/15 text-[#7FC3FF] hover:bg-[#0A8CF2]/25 font-semibold" : "border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"}`}>
                <Building2 className="mr-1.5 h-4 w-4" />
                <span className="text-left leading-tight">
                  {s.name}{stats?.store_counts?.[s.id] != null ? ` (${stats.store_counts[s.id]})` : ""}
                  {cc && <span className="block text-[10px] font-mono-tech font-normal opacity-80" data-testid={`store-split-${s.id}`}>{cc.New} new · {cc.Used} used</span>}
                </span>
              </Button>
            );
          })}
          {[
            ["new", stats?.new_recent_count, `Vehicles that hit the lot in the last ${NEW_DAYS} days (by your website's lot date)`],
            ["unprinted", stats?.unprinted_count, "Vehicles that have never had a sticker printed"],
            ["asis", stats?.as_is_count, "Units flagged As-Is with an inspection repair estimate"],
            ...(stores.length ? [["unassigned", stats?.unassigned_count, "Units that matched no store rule — their leads go to the group default inbox"]] : []),
            ["archived", stats?.archived_count, "Vehicles that left the dealer website — their QR codes now send scanners to Vinconex"],
          ].map(([key, count, title]) => {
            const Icon = VIEWS[key].icon;
            const on = view === key;
            return (
              <Button
                key={key}
                variant="outline"
                onClick={() => setView(on ? "all" : key)}
                data-testid={`filter-${key}`}
                aria-pressed={on}
                title={title}
                className={on ? VIEWS[key].cls : "border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"}
              >
                <Icon className="mr-1.5 h-4 w-4" /> {VIEWS[key].label}{count ? ` (${count})` : ""}
              </Button>
            );
          })}
        </div>
      </div>

      {(() => {
        const m = !loading ? codeMatches(q, vehicles) : null;
        if (!m) return null;
        return (
          <p data-testid="multi-code-note" className={`mb-4 text-xs flex items-start gap-2 ${m.missing.length ? "text-amber-300/90" : "text-emerald-300/90"}`}>
            <Search className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              {m.terms.length - m.missing.length} of {m.terms.length} codes matched{vehicles.length ? ` · ${vehicles.length} vehicle${vehicles.length === 1 ? "" : "s"} loaded` : ""}.
              {m.missing.length > 0 && <> Not found: <span className="font-mono-tech" data-testid="multi-code-missing">{m.missing.join(", ")}</span></>}
              {vehicles.length > 0 && <> Use <b>Select all shown</b> below to print them together.</>}
            </span>
          </p>
        );
      })()}

      {archived && !loading && (
        <p data-testid="archived-note" className="mb-4 text-xs text-amber-300/90 flex items-center gap-2">
          <Archive className="h-3.5 w-3.5" /> These vehicles left the dealer website during an overnight scan. Their QR stickers still work — scanners land on the Vinconex home page with a link to your new arrivals.
        </p>
      )}

      {/* Selection bar */}
      {!loading && vehicles.length > 0 && (
        <div
          data-testid="selection-bar"
          className="flex flex-wrap items-center gap-3 mb-5 rounded-lg border border-[#2A2E3D] bg-[#12141C]/70 px-4 py-2.5 text-sm"
        >
          <button
            onClick={allShownSelected ? clearSelection : selectAllShown}
            data-testid="select-all-btn"
            className="flex items-center gap-2 text-slate-200 hover:text-white"
          >
            {allShownSelected ? <CheckSquare className="h-4 w-4 text-[#0A8CF2]" /> : <Square className="h-4 w-4" />}
            {allShownSelected ? "Deselect all" : `Select all ${vehicles.length} shown`}
          </button>
          <span className="font-mono-tech text-xs text-slate-500" data-testid="selected-count">
            {selected.size} selected for printing
          </span>
          {selected.size > 0 && (
            <button
              onClick={clearSelection}
              data-testid="clear-selection-btn"
              className="ml-auto flex items-center gap-1 text-xs text-slate-400 hover:text-white"
            >
              <X className="h-3.5 w-3.5" /> Clear
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#0A8CF2]" />
        </div>
      ) : vehicles.length === 0 ? (
        <div className="text-center py-24 rounded-xl border border-dashed border-[#2A2E3D]">
          <Car className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">{view === "all" && !q ? "No vehicles yet" : "Nothing matches this view"}</p>
          <p className="text-sm text-slate-500 mt-1">
            {view === "unprinted" ? "Every active vehicle has had a sticker printed." : view === "new" ? "No vehicles arrived in the last 7 days." : view === "asis" ? "No units are flagged As-Is." : view === "unassigned" ? "Every unit is assigned to a store." : view.startsWith("store:") ? "No units route to this store yet." : q ? "Try another VIN, stock # or model." : "Click “Sync now” to pull inventory from your site."}
          </p>
        </div>
      ) : (
        <div data-testid="inventory-grid" className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {vehicles.map((v, i) => {
            const isSel = selected.has(v.id);
            return (
            <motion.div
              key={v.id}
              data-testid={`vehicle-card-${v.id}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.4) }}
              className={`group rounded-xl overflow-hidden border bg-[#12141C] hover:shadow-[0_0_20px_rgba(10,140,242,0.2)] transition-all ${
                isSel ? "border-[#0A8CF2] ring-1 ring-[#0A8CF2]/50" : "border-[#2A2E3D] hover:border-[#0A8CF2]"
              }`}
            >
              <div className="relative aspect-[16/10] bg-black overflow-hidden">
                {v.image ? (
                  <img
                    src={v.image}
                    alt={v.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-600">
                    <Car className="h-8 w-8" />
                  </div>
                )}
                <Badge className={`absolute top-2 left-2 text-white ${v.active === false ? "bg-amber-600 hover:bg-amber-600" : "bg-[#0A8CF2] hover:bg-[#0A8CF2]"}`}>
                  {v.active === false ? `Archived${v.archived_at ? ` · ${timeAgo(v.archived_at)}` : ""}` : v.condition}
                </Badge>
                {v.as_is && (
                  <Badge data-testid={`as-is-badge-${v.id}`} title={v.as_is_notes || "Sold As-Is"} className="absolute top-2 left-[4.6rem] bg-amber-500 hover:bg-amber-500 text-black font-bold flex items-center gap-1">
                    <Wrench className="h-3 w-3" /> AS-IS{v.repair_estimate_total ? ` · ${money(v.repair_estimate_total)}` : ""}
                  </Badge>
                )}
                <div
                  data-testid={`print-status-${v.id}`}
                  title={v.printed_at ? `Sticker printed ${new Date(v.printed_at).toLocaleString()} (${v.print_count || 1}×)` : "No sticker printed yet"}
                  className={`absolute bottom-2 right-2 flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-mono-tech backdrop-blur border ${
                    v.printed_at ? "bg-black/60 border-white/15 text-slate-300" : "bg-amber-500/90 border-amber-400 text-black font-bold"
                  }`}
                >
                  <Printer className="h-3 w-3" /> {v.printed_at ? `printed ${timeAgo(v.printed_at)}` : "not printed"}
                </div>
                <div
                  data-testid={`scan-stats-${v.id}`}
                  title={v.last_scanned_at ? `Last scanned ${new Date(v.last_scanned_at).toLocaleString()}` : "No QR scans yet"}
                  className={`absolute bottom-2 left-2 flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-mono-tech backdrop-blur border ${
                    v.scan_count > 0 ? "bg-[#0A8CF2]/80 border-[#0A8CF2] text-white" : "bg-black/60 border-white/15 text-slate-300"
                  }`}
                >
                  <ScanLine className="h-3 w-3" />
                  <span data-testid={`scan-count-${v.id}`}>{v.scan_count || 0}</span>
                  <span className="opacity-80">{v.scan_count === 1 ? "scan" : "scans"}</span>
                  {v.last_scanned_at && <span className="opacity-70">· {timeAgo(v.last_scanned_at)}</span>}
                </div>
                <button
                  onClick={() => toggleSelect(v.id)}
                  data-testid={`select-vehicle-${v.id}`}
                  aria-pressed={isSel}
                  title={isSel ? "Deselect for printing" : "Select for printing"}
                  className={`absolute top-2 right-2 h-8 w-8 rounded-md flex items-center justify-center border backdrop-blur transition-colors ${
                    isSel ? "bg-[#0A8CF2] border-[#0A8CF2] text-white" : "bg-black/60 border-white/20 text-white hover:bg-black/80"
                  }`}
                >
                  {isSel ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                </button>
              </div>
              <div className="p-4">
                <div className="font-semibold truncate">
                  {v.year} {v.make} {v.model}
                </div>
                <div className="text-xs text-slate-500 truncate">{v.trim}</div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[#0A8CF2] font-bold text-lg">{priceLabel(v.price)}</span>
                  {v.mileage != null && (
                    <span className="text-xs text-slate-500">{v.mileage.toLocaleString()} mi</span>
                  )}
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="font-mono-tech text-[10px] text-slate-600 truncate">VIN {v.vin}</div>
                  <StockNumberField
                    vehicle={v}
                    onSaved={(u) => setVehicles((list) => list.map((x) => (x.id === u.id ? u : x)))}
                  />
                </div>
                {stores.length > 0 && (
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-slate-600 font-mono-tech">Leads →</span>
                    <StoreChip vehicle={v} stores={stores} onSaved={(u) => { replaceVehicle(u); loadStats(); }} />
                  </div>
                )}
                <div className="mt-4 flex gap-2">
                  <Button
                    onClick={() => setQrVehicle(v)}
                    data-testid={`qr-btn-${v.id}`}
                    className="flex-1 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-semibold h-9"
                  >
                    <QrCode className="mr-2 h-4 w-4" /> QR Code
                  </Button>
                  <a href={`/sticker/${v.id}`} target="_blank" rel="noreferrer" title="Print this sticker">
                    <Button
                      variant="outline"
                      data-testid={`print-one-${v.id}`}
                      className="h-9 w-9 px-0 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"
                    >
                      <Printer className="h-4 w-4" />
                    </Button>
                  </a>
                  <Button
                    variant="outline"
                    onClick={() => setAsIsVehicle(v)}
                    data-testid={`as-is-btn-${v.id}`}
                    title={v.as_is ? "Edit As-Is estimate" : "Flag As-Is / inspection estimate"}
                    className={`h-9 w-9 px-0 ${v.as_is ? "border-amber-500/70 bg-amber-950/40 hover:bg-amber-950/60 text-amber-300" : "border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"}`}
                  >
                    <Wrench className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setInsiderVehicle(v)}
                    data-testid={`insider-btn-${v.id}`}
                    title={insiderCount(v) ? "Edit insider details (recon, acquisition, time on lot)" : "Add insider details shoppers can unlock"}
                    className={`h-9 w-9 px-0 ${insiderCount(v) ? "border-[#0A8CF2]/70 bg-[#0A8CF2]/15 hover:bg-[#0A8CF2]/25 text-[#7FC3FF]" : "border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"}`}
                  >
                    <ClipboardList className="h-4 w-4" />
                  </Button>
                  <a href={`/v/${v.id}`} target="_blank" rel="noreferrer">
                    <Button
                      variant="outline"
                      className="h-9 w-9 px-0 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </a>
                </div>
                <button
                  onClick={() => markPrinted(v, !v.printed_at)}
                  data-testid={`mark-printed-${v.id}`}
                  className="mt-2 w-full text-[11px] text-slate-500 hover:text-slate-200 text-left inline-flex items-center gap-1.5"
                >
                  <PrinterCheck className="h-3 w-3" /> {v.printed_at ? "Mark as not printed" : "Mark sticker as printed"}
                </button>
              </div>
            </motion.div>
            );
          })}
        </div>
      )}

      {qrVehicle && (
        <QRModal vehicle={qrVehicle} open={!!qrVehicle} onOpenChange={(o) => !o && setQrVehicle(null)} />
      )}
      <AsIsDialog vehicle={asIsVehicle} open={!!asIsVehicle} onOpenChange={(o) => !o && setAsIsVehicle(null)} onSaved={(u) => { replaceVehicle(u); loadStats(); }} />
      <InsiderDialog vehicle={insiderVehicle} walls={walls} open={!!insiderVehicle} onOpenChange={(o) => !o && setInsiderVehicle(null)} onSaved={replaceVehicle} />
    </div>
  );
}
